import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { TicketStatus } from '@t3/shared';
import { ArrowLeft, CalendarClock, CheckCircle2, Circle, Flag, Star } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card, StatusBadge } from '../components/Card.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { ProjectProgressBar } from '../components/ProjectProgressBar.tsx';

interface PortalContact {
  id: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface PortalProject {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'cancelled';
  billingMode: 'flat_fee' | 'block_hours';
  budgetedHours: number;
  consumedMinutes: number;
  startedAt: string;
  completedAt: string | null;
  dueDate: string | null;
  cutoverDate: string | null;
  subtaskTotal: number;
  subtaskCompleted: number;
  /** 0-100. Computed on the server by the same function the team's panel uses. */
  progressPercent: number;
}

interface PortalSubtask {
  id: string;
  milestoneId: string | null;
  title: string;
  dueDate: string | null;
  assignedAgentName: string | null;
  completedAt: string | null;
  sortOrder: number;
}

interface PortalMilestone {
  id: string;
  title: string;
  milestoneDate: string;
  completedAt: string | null;
}

/** One milestone and its tasks, checked off. `null` for tasks with no milestone. */
function MilestoneTasks({
  milestone,
  tasks,
}: {
  milestone: PortalMilestone | null;
  tasks: PortalSubtask[];
}): React.ReactElement {
  const done = milestone ? Boolean(milestone.completedAt) : false;
  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--color-border)] pb-1.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-header)]">
          {milestone && (
            <Star
              className={`h-4 w-4 shrink-0 ${done ? 'text-emerald-500' : 'text-amber-500'}`}
              fill={done ? 'currentColor' : 'none'}
              aria-hidden="true"
            />
          )}
          {milestone ? milestone.title : 'Other tasks'}
          {done && <span className="text-xs font-normal text-emerald-700">Complete</span>}
        </h3>
        {milestone && (
          <span className="text-xs text-[var(--color-muted)]">
            {done ? `Done ${fmtDate(milestone.completedAt)}` : `Target ${fmtDate(milestone.milestoneDate)}`}
          </span>
        )}
      </div>
      {tasks.length > 0 && (
        <ul className="mt-1">
          {tasks.map((s) => (
            <li key={s.id} className="flex items-start gap-3 py-1.5 text-sm">
              {s.completedAt ? (
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500"
                  aria-label="Done"
                />
              ) : (
                <Circle
                  className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-muted)]"
                  aria-label="Not done yet"
                />
              )}
              <span
                className={
                  s.completedAt
                    ? 'flex-1 text-[var(--color-muted)] line-through'
                    : 'flex-1 text-[var(--color-body)]'
                }
              >
                {s.title}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface PortalTicketLite {
  id: string;
  displayNumber: number;
  subject: string;
  status: TicketStatus;
  updatedAt: string;
}

interface DetailResponse {
  project: PortalProject;
  subtasks: PortalSubtask[];
  milestones: PortalMilestone[];
  tickets: PortalTicketLite[];
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
  return d.toLocaleDateString();
}

function statusPill(status: PortalProject['status']): React.ReactElement {
  const cls =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'completed'
        ? 'bg-slate-100 text-slate-700'
        : 'bg-[var(--color-surface)] text-[var(--color-muted)]';
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${cls}`}>
      {status}
    </span>
  );
}

export function PortalProjectDetailPage(): React.ReactElement {
  useDocumentTitle('Project');
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then((r) => {
        if (cancelled) return;
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: `/portal/projects/${id}` } });
          return;
        }
        setContact(r.contact);
      })
      .catch(() => {
        if (!cancelled) navigate('/login', { replace: true });
      });
    apiFetch<DetailResponse>(`/api/portal/projects/${id}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Failed to load project.');
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  return (
    <div className="min-h-screen bg-[var(--color-surface)]">
      <PortalNavBar contact={contact} />
      <PortalHealthBanner />
      <main className="mx-auto max-w-5xl space-y-6 px-6 py-8">
        <div>
          <Link
            to="/portal/projects"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-muted)] hover:text-[var(--color-header)]"
          >
            <ArrowLeft className="h-4 w-4" /> All projects
          </Link>
        </div>

        {error && (
          <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          </Card>
        )}
        {!data && !error && <p className="text-[var(--color-muted)]">Loading…</p>}

        {data && (
          <>
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="text-3xl font-bold text-[var(--color-header)]">
                    {data.project.name}
                  </h1>
                  <p className="mt-1 text-sm text-[var(--color-muted)]">
                    Started {fmtDate(data.project.startedAt)}
                    {data.project.completedAt
                      ? ` · Completed ${fmtDate(data.project.completedAt)}`
                      : ''}
                  </p>
                </div>
                {statusPill(data.project.status)}
              </div>

              <div className="mt-5">
                <ProjectProgressBar
                  percent={data.project.progressPercent}
                  done={data.subtasks.filter((s) => s.completedAt).length}
                  total={data.subtasks.length}
                />
              </div>

              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Due date</dt>
                  <dd className="mt-1 inline-flex items-center gap-1.5 text-base font-semibold text-[var(--color-header)]">
                    <CalendarClock className="h-4 w-4 text-[var(--color-muted)]" />
                    {fmtDate(data.project.dueDate)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Cutover date</dt>
                  <dd className="mt-1 inline-flex items-center gap-1.5 text-base font-semibold text-[var(--color-header)]">
                    <Flag className="h-4 w-4 text-[var(--color-muted)]" />
                    {fmtDate(data.project.cutoverDate)}
                  </dd>
                </div>

              </dl>

              {data.project.billingMode === 'flat_fee' ? (
                <p className="mt-5 rounded-[var(--radius-button)] bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--color-muted)]">
                  This project is billed at a flat fee. Hours are tracked internally to gauge
                  progress; you're not charged per hour.
                </p>
              ) : (
                <div className="mt-5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
                      Hours used
                    </span>
                    <span className="text-sm font-semibold text-[var(--color-header)]">
                      {(data.project.consumedMinutes / 60).toFixed(2)}h /{' '}
                      {data.project.budgetedHours}h budgeted
                    </span>
                  </div>
                  {data.project.budgetedHours > 0 && (
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[var(--color-surface)]">
                      <div
                        className="h-full bg-[var(--color-t3-blue)] transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            (data.project.consumedMinutes / 60 / data.project.budgetedHours) * 100,
                          )}%`,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </Card>

            {(data.milestones.length > 0 || data.subtasks.length > 0) && (
              <Card>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Task list</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Ticked off by our team as the work is done.
                </p>
                <div className="mt-4 space-y-5">
                  {data.milestones.map((m) => (
                    <MilestoneTasks
                      key={m.id}
                      milestone={m}
                      tasks={data.subtasks.filter((s) => s.milestoneId === m.id)}
                    />
                  ))}
                  {(() => {
                    const loose = data.subtasks.filter(
                      (s) => !s.milestoneId || !data.milestones.some((m) => m.id === s.milestoneId),
                    );
                    return loose.length > 0 ? (
                      <MilestoneTasks milestone={null} tasks={loose} />
                    ) : null;
                  })()}
                </div>
              </Card>
            )}
            {data.tickets.length > 0 && (
              <Card>
                <h2 className="text-base font-semibold text-[var(--color-header)]">
                  Related tickets
                </h2>
                <ul className="mt-3 divide-y divide-[var(--color-border)]">
                  {data.tickets.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                      <Link
                        to={`/portal/tickets/${t.id}`}
                        className="flex-1 truncate font-medium text-[var(--color-t3-blue)] hover:underline"
                      >
                        T-{t.displayNumber} · {t.subject}
                      </Link>
                      <StatusBadge status={t.status}>{t.status.replace(/_/g, ' ')}</StatusBadge>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </>
        )}
      </main>
      <PortalFooter />
    </div>
  );
}
